/**
 * Utilidades de fecha para timezone America/Santiago.
 * Chile usa CLT (UTC-4) en invierno y CLST (UTC-3) en verano — el offset varía con DST.
 * Esta función resuelve el offset real para cualquier fecha sin hardcodear el valor.
 */

/**
 * Retorna el offset UTC de America/Santiago para una fecha dada.
 * Ejemplo: '-04:00' en invierno, '-03:00' en verano.
 */
export function getSantiagoOffset(dateStr: string): string {
  // Construir una instancia UTC al mediodía de la fecha objetivo para evitar
  // problemas de borde en cambio de día por DST
  const probeUTC = new Date(`${dateStr}T12:00:00Z`);

  // Obtener la representación en America/Santiago con el offset explícito
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Santiago',
    timeZoneName: 'shortOffset'
  });

  const tzPart = formatter
    .formatToParts(probeUTC)
    .find(p => p.type === 'timeZoneName');

  if (!tzPart) return '-04:00'; // fallback conservador (CLT invierno)

  // tzPart.value es "GMT-3" o "GMT-4"
  const match = tzPart.value.match(/GMT([+-])(\d+)(?::(\d+))?/);
  if (!match) return '-04:00';

  const sign  = match[1];                            // '+' o '-'
  const hours = match[2].padStart(2, '0');
  const mins  = (match[3] ?? '00').padStart(2, '0');
  return `${sign}${hours}:${mins}`;
}

/**
 * Construye un ISO string con el offset correcto de America/Santiago.
 * Usa: toSantiagoISO('2026-04-27', '09:00') → '2026-04-27T09:00:00-04:00'
 */
export function toSantiagoISO(dateStr: string, timeStr: string): string {
  return `${dateStr}T${timeStr}:00${getSantiagoOffset(dateStr)}`;
}

/**
 * Retorna la fecha en America/Santiago como 'YYYY-MM-DD'.
 * Uso: toSantiagoDateStr(new Date('2026-06-04T15:00:00Z')) → '2026-06-04'
 */
export function toSantiagoDateStr(date: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Santiago' }).format(date);
}

/**
 * Retorna la hora en America/Santiago como 'HH:MM'.
 * Uso: toSantiagoTime(new Date('2026-06-04T15:00:00Z')) → '11:00'
 */
export function toSantiagoTime(date: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'America/Santiago',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}
